import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiBarChart2,
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiLayers,
  FiSearch,
  FiUser,
  FiUsers,
  FiAlertTriangle,
  FiTrendingUp,
  FiEye,
  FiRefreshCw,
  FiFilter,
  FiPrinter,
  FiArrowRight,
  FiChevronDown,
  FiChevronUp,
  FiCalendar,
  FiCheck,
  FiX,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import syllabusService from "../../services/syllabusService";
import courseService from "../../services/courseService";
import employeeService from "../../services/employeeService";

const STATUS_CONFIG = {
  "Completed On Time": { bg: "#dcfce7", color: "#15803d", border: "#86efac", icon: FiCheckCircle },
  "Completed (Over Hours)": { bg: "#fef9c3", color: "#854d0e", border: "#fde047", icon: FiAlertTriangle },
  "Ahead of Schedule": { bg: "#e0e7ff", color: "#3730a3", border: "#a5b4fc", icon: FiTrendingUp },
  "On Track": { bg: "#e8f1f8", color: "#1e40af", border: "#93c5fd", icon: FiClock },
  "Over Hours": { bg: "#ffedd5", color: "#9a3412", border: "#fdba74", icon: FiAlertTriangle },
  "Behind Schedule": { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", icon: FiAlertTriangle },
  "Not Started": { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb", icon: FiClock },
  "No Subjects": { bg: "#f3f4f6", color: "#9ca3af", border: "#e5e7eb", icon: FiUser },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["Not Started"];
  const Icon = cfg.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        padding: "3px 9px",
        borderRadius: 99,
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} />
      {status}
    </span>
  );
}

function ProgressBar({ percent, color = "#003b67", height = 8 }) {
  const safePercent = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div
      style={{
        width: "100%",
        height,
        background: "#e2e8f0",
        borderRadius: 99,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          width: `${safePercent}%`,
          height: "100%",
          background: color,
          borderRadius: 99,
          transition: "width 0.4s ease",
        }}
      />
    </div>
  );
}

export default function TeacherPerformancePage() {
  const { academicYear, academicYears } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState("teachers"); // "teachers" | "subjects"
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data states
  const [summary, setSummary] = useState(null);
  const [teacherReports, setTeacherReports] = useState([]);
  const [allProgress, setAllProgress] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachersList, setTeachersList] = useState([]);

  // Filters
  const [selectedAY, setSelectedAY] = useState(academicYear?._id || "");
  const [courseFilter, setCourseFilter] = useState(searchParams.get("course") || "");
  const [yearNumberFilter, setYearNumberFilter] = useState(searchParams.get("yearNumber") || "");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [teacherFilter, setTeacherFilter] = useState(searchParams.get("teacher") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  // Modal for Unit-Level Drill-Down
  const [drilldownSubject, setDrilldownSubject] = useState(null);
  const [drilldownTeacher, setDrilldownTeacher] = useState(null);

  // Sync active academic year from context whenever academicYear changes
  useEffect(() => {
    if (academicYear?._id) {
      setSelectedAY(academicYear._id);
    }
  }, [academicYear]);

  // Load courses and active teachers for filters (reloads when active year/college context changes)
  useEffect(() => {
    courseService
      .getAll({ isActive: true })
      .then((res) => setCourses(res.data || []))
      .catch(() => {});

    employeeService
      .getAll({ limit: 500, employeeCategory: "Teaching", status: "Active" })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const teachingList = (Array.isArray(list) ? list : []).filter(
          (t) => (t.employeeCategory || "").trim().toLowerCase() === "teaching"
        );
        setTeachersList(teachingList);
      })
      .catch(() => {});
  }, [academicYear]);

  // Fetch performance report & summary
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ayId = selectedAY || academicYear?._id || "";
      const [sumRes, reportRes, progressRes] = await Promise.all([
        syllabusService.getSummary(ayId ? { academicYear: ayId } : {}),
        syllabusService.getTeacherPerformance({
          ...(ayId ? { academicYear: ayId } : {}),
          department: departmentFilter,
          search,
        }),
        syllabusService.getAll({
          ...(ayId ? { academicYear: ayId } : {}),
          course: courseFilter,
          yearNumber: yearNumberFilter,
          teacher: teacherFilter,
          status: statusFilter,
          search,
        }),
      ]);

      const extractData = (res) => {
        if (!res) return null;
        if (Array.isArray(res)) return res;
        if (Array.isArray(res.data)) return res.data;
        if (res.data && typeof res.data === "object") return res.data;
        return res;
      };

      const sumData = extractData(sumRes);
      const repData = extractData(reportRes);
      const progData = extractData(progressRes);

      setSummary(sumData || null);
      setTeacherReports(Array.isArray(repData) ? repData : (repData?.data || []));
      setAllProgress(Array.isArray(progData) ? progData : (progData?.data || []));
    } catch (err) {
      console.error("Failed to load syllabus tracking data:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to load syllabus tracking data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    selectedAY,
    academicYear,
    departmentFilter,
    courseFilter,
    yearNumberFilter,
    teacherFilter,
    statusFilter,
    search,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Combined faculty options from Employee catalog + syllabus progress records (Teaching staff only)
  const allFacultyOptions = useMemo(() => {
    const teachingMap = new Map();

    // 1. Add all verified teaching employees from the master employee list
    (teachersList || []).forEach((t) => {
      if (t?._id && (t.employeeCategory || "").trim().toLowerCase() === "teaching") {
        teachingMap.set(String(t._id), t);
      }
    });

    // 2. Only add from teacherReports if explicitly marked as Teaching
    (teacherReports || []).forEach((r) => {
      if (r?.teacher?._id && !teachingMap.has(String(r.teacher._id))) {
        if ((r.teacher.employeeCategory || "").trim().toLowerCase() === "teaching") {
          teachingMap.set(String(r.teacher._id), r.teacher);
        }
      }
    });

    // 3. Only add from allProgress if explicitly marked as Teaching
    (allProgress || []).forEach((p) => {
      if (p?.teacher?._id && !teachingMap.has(String(p.teacher._id))) {
        if ((p.teacher.employeeCategory || "").trim().toLowerCase() === "teaching") {
          teachingMap.set(String(p.teacher._id), p.teacher);
        }
      }
    });

    return Array.from(teachingMap.values()).sort((a, b) =>
      (a.fullName || "").localeCompare(b.fullName || ""),
    );
  }, [teachersList, teacherReports, allProgress]);

  // Filtered teachers list based on status & teacher selection (Teaching staff only)
  const filteredTeacherReports = useMemo(() => {
    const teachingIds = new Set(allFacultyOptions.map((t) => String(t._id)));
    return teacherReports.filter((item) => {
      const tId = String(item?.teacher?._id || item?.teacher || "");
      const cat = (item?.teacher?.employeeCategory || "").trim().toLowerCase();
      if (cat === "non teaching") return false;
      if (allFacultyOptions.length > 0 && !teachingIds.has(tId)) return false;
      if (teacherFilter && tId !== String(teacherFilter)) return false;
      if (statusFilter && item.performanceStatus !== statusFilter) return false;
      return true;
    });
  }, [teacherReports, teacherFilter, statusFilter, allFacultyOptions]);

  // Filtered progress list for Tab 2 (Teaching staff only)
  const filteredProgress = useMemo(() => {
    const teachingIds = new Set(allFacultyOptions.map((t) => String(t._id)));
    return allProgress.filter((p) => {
      const tId = String(p?.teacher?._id || p?.teacher || "");
      const cat = (p?.teacher?.employeeCategory || "").trim().toLowerCase();
      if (cat === "non teaching") return false;
      if (allFacultyOptions.length > 0 && !teachingIds.has(tId)) return false;
      if (teacherFilter && tId !== String(teacherFilter)) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      return true;
    });
  }, [allProgress, teacherFilter, statusFilter, allFacultyOptions]);

  // Overall department options
  const departmentOptions = useMemo(() => {
    const set = new Set();
    allFacultyOptions.forEach((t) => {
      if (t?.department) set.add(t.department);
    });
    return Array.from(set).sort();
  }, [allFacultyOptions]);


  // Handle printing summary report
  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FiTrendingUp color="#003b67" /> Faculty Syllabus & Performance Tracking
          </h1>
          <p className="page-subtitle">
            Track teacher progress in completing subject syllabi, planned vs actual lecture hours, and pacing status.
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          {/* Academic Year Selector */}
          <select
            className="form-control form-control-sm"
            style={{ width: "auto", minWidth: 140, fontWeight: 600 }}
            value={selectedAY}
            onChange={(e) => setSelectedAY(e.target.value)}
          >
            {academicYears?.map((ay) => (
              <option key={ay._id} value={ay._id}>
                {ay.label} {ay.isActive ? "(Active)" : ""}
              </option>
            ))}
          </select>

          <button className="btn btn-outline btn-sm" onClick={handlePrint} title="Print report">
            <FiPrinter size={13} /> Print Report
          </button>

          <button
            className="btn btn-outline btn-sm"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh statistics"
          >
            <FiRefreshCw size={13} className={refreshing ? "spin" : ""} />
          </button>
        </div>
      </div>

      {/* ── KPI Summary Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.25rem",
        }}
      >
        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #003b67" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Avg. Completion</span>
            <FiBarChart2 color="#003b67" size={16} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#003b67", marginTop: 4 }}>
            {summary?.avgCompletionRate || 0}%
          </div>
          <ProgressBar percent={summary?.avgCompletionRate || 0} color="#003b67" height={6} />
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            Across {summary?.totalTracked || 0} active subject syllabus records
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #16a34a" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Fully Completed</span>
            <FiCheckCircle color="#16a34a" size={16} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#16a34a", marginTop: 4 }}>
            {summary?.completedCount || 0}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
            Syllabi 100% finished
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #d97706" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>In Progress</span>
            <FiClock color="#d97706" size={16} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#d97706", marginTop: 4 }}>
            {summary?.inProgressCount || 0}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
            Syllabi currently being delivered
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #7c3aed" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Total Hours Logged</span>
            <FiClock color="#7c3aed" size={16} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#7c3aed", marginTop: 4 }}>
            {summary?.totalActualHours || 0}h
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", marginLeft: 4 }}>
              / {summary?.totalPlannedHours || 0}h planned
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
            {summary?.totalActualHours > summary?.totalPlannedHours
              ? `+${summary?.totalActualHours - summary?.totalPlannedHours}h overtime`
              : `${(summary?.totalPlannedHours || 0) - (summary?.totalActualHours || 0)}h remaining`}
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #0891b2" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Faculty Active</span>
            <FiUsers color="#0891b2" size={16} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#0891b2", marginTop: 4 }}>
            {summary?.activeTeachersCount || 0}
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginLeft: 4 }}>
              / {summary?.totalTeachers || 0}
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
            Teachers tracking syllabus
          </div>
        </div>
      </div>

      {/* ── Filters & Tabs Bar ── */}
      <div className="card" style={{ marginBottom: "1rem", padding: "0.85rem 1rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginBottom: "0.75rem",
          }}
        >
          {/* Tab buttons */}
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button
              className={`btn btn-sm ${activeTab === "teachers" ? "btn-primary" : "btn-outline"}`}
              onClick={() => setActiveTab("teachers")}
              style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
            >
              <FiUser size={13} /> Faculty Performance Matrix ({filteredTeacherReports.length})
            </button>
            <button
              className={`btn btn-sm ${activeTab === "subjects" ? "btn-primary" : "btn-outline"}`}
              onClick={() => setActiveTab("subjects")}
              style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
            >
              <FiLayers size={13} /> Subject-Wise Syllabus Grid ({filteredProgress.length})
            </button>

          </div>

          {/* Search box */}
          <div style={{ position: "relative", minWidth: 220, flex: 1, maxWidth: 320 }}>
            <FiSearch
              size={13}
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
            />
            <input
              className="form-control form-control-sm"
              placeholder="Search teacher, subject, course..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 28 }}
            />
          </div>
        </div>

        {/* Secondary Filter Dropdowns */}
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: 12, color: "var(--text-muted)" }}>
            <FiFilter size={12} /> Filters:
          </div>

          {activeTab === "teachers" && departmentOptions.length > 0 && (
            <select
              className="form-control form-control-sm"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="">All Departments</option>
              {departmentOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}

          {activeTab === "subjects" && (
            <>
              <select
                className="form-control form-control-sm"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                style={{ width: "auto" }}
              >
                <option value="">All Courses</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.shortName || c.courseName}
                  </option>
                ))}
              </select>

              <select
                className="form-control form-control-sm"
                value={yearNumberFilter}
                onChange={(e) => setYearNumberFilter(e.target.value)}
                style={{ width: "auto" }}
              >
                <option value="">All Years</option>
                <option value="1">Year 1</option>
                <option value="2">Year 2</option>
                <option value="3">Year 3</option>
                <option value="4">Year 4</option>
              </select>
            </>
          )}

          <select
            className="form-control form-control-sm"
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
            style={{ width: "auto", maxWidth: 180 }}
          >
            <option value="">All Faculty</option>
            {allFacultyOptions.map((t) => (
              <option key={t._id} value={t._id}>
                {t.fullName} ({t.employeeCode || "Teacher"})
              </option>
            ))}
          </select>


          <select
            className="form-control form-control-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: "auto" }}
          >
            <option value="">All Statuses</option>
            <option value="On Track">On Track</option>
            <option value="Ahead of Schedule">Ahead of Schedule</option>
            <option value="Completed On Time">Completed On Time</option>
            <option value="Over Hours">Over Hours</option>
            <option value="Behind Schedule">Behind Schedule</option>
            <option value="Not Started">Not Started</option>
          </select>

          {(courseFilter || yearNumberFilter || departmentFilter || teacherFilter || statusFilter || search) && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                setCourseFilter("");
                setYearNumberFilter("");
                setDepartmentFilter("");
                setTeacherFilter("");
                setStatusFilter("");
                setSearch("");
              }}
              style={{ fontSize: 11, padding: "2px 8px" }}
            >
              <FiX size={11} /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── Content Area ── */}
      {loading ? (
        <div className="loading-center">
          <div className="spinner" />
          Loading faculty performance data…
        </div>
      ) : activeTab === "teachers" ? (
        /* ═════════════════════════════════════════════════════════════════════════
           TAB 1: Faculty Performance Matrix
           ═════════════════════════════════════════════════════════════════════════ */
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Faculty Member</th>
                  <th>Department & Designation</th>
                  <th style={{ textAlign: "center" }}>Subjects Assigned</th>
                  <th style={{ textAlign: "center" }}>Units Completed</th>
                  <th style={{ minWidth: 160 }}>Syllabus Completion</th>
                  <th style={{ textAlign: "center" }}>Planned vs Actual Hrs</th>
                  <th style={{ textAlign: "center" }}>Variance</th>
                  <th style={{ textAlign: "center" }}>Pacing Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeacherReports.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
                      <FiUsers size={36} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.5rem" }} />
                      No faculty records match the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredTeacherReports.map((item, idx) => {
                    const initials =
                      item.teacher.fullName
                        ?.split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join("") || "T";

                    const variance = item.hourVariance;
                    const varianceColor =
                      variance > 5 ? "#dc2626" : variance > 0 ? "#d97706" : variance < 0 ? "#16a34a" : "var(--text-muted)";

                    return (
                      <tr key={item.teacher._id}>
                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{idx + 1}</td>

                        {/* Faculty Profile */}
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                background: "#ede9fe",
                                color: "#7c3aed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {item.teacher.photo?.url ? (
                                <img
                                  src={item.teacher.photo.url}
                                  alt={item.teacher.fullName}
                                  style={{ width: "100%", height: "100%", borderRadius: 10, objectFit: "cover" }}
                                />
                              ) : (
                                initials
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{item.teacher.fullName}</div>
                              <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                {item.teacher.employeeCode}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Department */}
                        <td>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{item.teacher.department}</div>
                          <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                            {item.teacher.designation || "Faculty"}
                          </div>
                        </td>

                        {/* Subjects Assigned */}
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              background: "#f1f5f9",
                              padding: "2px 8px",
                              borderRadius: 99,
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {item.assignedSubjectsCount ?? item.subjectsCount ?? item.subjects?.length ?? 0}
                          </span>
                        </td>


                        {/* Units Done */}
                        <td style={{ textAlign: "center", fontSize: 13, fontWeight: 600 }}>
                          {item.totalUnitsCompleted} / {item.totalUnitsAssigned}
                        </td>

                        {/* Progress */}
                        <td>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                            <span style={{ fontWeight: 700, color: item.completionPercentage === 100 ? "#16a34a" : "#003b67" }}>
                              {item.completionPercentage}%
                            </span>
                          </div>
                          <ProgressBar
                            percent={item.completionPercentage}
                            color={item.completionPercentage === 100 ? "#16a34a" : "#003b67"}
                            height={7}
                          />
                        </td>

                        {/* Hours */}
                        <td style={{ textAlign: "center", fontSize: 12.5 }}>
                          <strong>{item.totalActualHours}h</strong>
                          <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}> / {item.totalPlannedHours}h</span>
                        </td>

                        {/* Variance */}
                        <td style={{ textAlign: "center", fontSize: 12.5, fontWeight: 700, color: varianceColor }}>
                          {variance > 0 ? `+${variance}h` : variance < 0 ? `${variance}h` : "0h"}
                        </td>

                        {/* Status badge */}
                        <td style={{ textAlign: "center" }}>
                          <StatusBadge status={item.performanceStatus} />
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "right" }}>
                          {item.subjects.length > 0 && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => {
                                setDrilldownTeacher(item.teacher);
                                setDrilldownSubject(item.subjects[0]);
                              }}
                              style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                            >
                              <FiEye size={12} /> View Details
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ═════════════════════════════════════════════════════════════════════════
           TAB 2: Subject-Wise Syllabus Grid
           ═════════════════════════════════════════════════════════════════════════ */
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
          {filteredProgress.length === 0 ? (
            <div className="card" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "3rem" }}>
              <FiBookOpen size={40} style={{ opacity: 0.2, margin: "0 auto 0.75rem", display: "block" }} />
              <p style={{ color: "var(--text-muted)" }}>No subject progress found matching filters.</p>
            </div>
          ) : (
            filteredProgress.map((p) => {

              const variance = (p.totalActualHours || 0) - (p.totalPlannedHours || 0);
              const varianceColor =
                variance > 3 ? "#dc2626" : variance > 0 ? "#d97706" : variance < 0 ? "#16a34a" : "var(--text-muted)";

              return (
                <div
                  key={p._id}
                  className="card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "1rem",
                  }}
                >
                  <div>
                    {/* Course & Status tag */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <span
                        style={{
                          background: "#e0f2fe",
                          color: "#0369a1",
                          padding: "2px 8px",
                          borderRadius: 99,
                          fontSize: 11.5,
                          fontWeight: 700,
                        }}
                      >
                        {p.course?.shortName || p.course?.courseName || "Course"} · Year {p.yearNumber}
                      </span>
                      <StatusBadge status={p.status || "Not Started"} />
                    </div>

                    {/* Subject Title */}
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                      {p.subject?.name}
                    </h3>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                      Code: {p.subject?.code || "—"} · Type: {p.subject?.type || "Theory"} · Weekly: {p.subject?.weeklyHours || 0}h
                    </div>

                    {/* Teacher assigned */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        background: "#f8fafc",
                        padding: "0.5rem 0.65rem",
                        borderRadius: 8,
                        marginBottom: 12,
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 7,
                          background: "#ede9fe",
                          color: "#7c3aed",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {p.teacher?.fullName?.[0]?.toUpperCase() || "T"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.teacher?.fullName || "Unassigned"}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {p.teacher?.department || ""}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar & Stats */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: "var(--text-muted)" }}>
                          Units: <strong>{p.completedUnitsCount || 0}</strong> of <strong>{p.totalUnitsCount || 0}</strong> completed
                        </span>
                        <span style={{ fontWeight: 800, color: p.completionPercentage === 100 ? "#16a34a" : "#003b67" }}>
                          {p.completionPercentage || 0}%
                        </span>
                      </div>
                      <ProgressBar
                        percent={p.completionPercentage || 0}
                        color={p.completionPercentage === 100 ? "#16a34a" : "#003b67"}
                        height={8}
                      />
                    </div>

                    {/* Hours details */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        padding: "6px 0",
                        borderTop: "1px dashed var(--border)",
                        marginTop: 8,
                      }}
                    >
                      <span style={{ color: "var(--text-muted)" }}>Actual vs Planned Hours:</span>
                      <span>
                        <strong>{p.totalActualHours || 0}h</strong> / {p.totalPlannedHours || 0}h
                        <span style={{ marginLeft: 6, fontWeight: 700, color: varianceColor }}>
                          ({variance > 0 ? `+${variance}h` : `${variance}h`})
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: "0.4rem", marginTop: 12 }}>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ flex: 1, justifyContent: "center", fontSize: 12 }}
                      onClick={() => {
                        setDrilldownTeacher(p.teacher);
                        setDrilldownSubject({
                          progressId: p._id,
                          subjectId: p.subject?._id,
                          subjectName: p.subject?.name,
                          subjectCode: p.subject?.code,
                          course: p.course?.shortName || p.course?.courseName,
                          yearNumber: p.yearNumber,
                          semesterNumber: p.semesterNumber,
                          totalUnits: p.totalUnitsCount,
                          completedUnits: p.completedUnitsCount,
                          completionPercentage: p.completionPercentage,
                          plannedHours: p.totalPlannedHours,
                          actualHours: p.totalActualHours,
                          status: p.status,
                          units: p.units,
                        });
                      }}
                    >
                      <FiEye size={12} /> Unit Breakdown
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ justifyContent: "center", fontSize: 12 }}
                      onClick={() => navigate(`/subjects/${p.subject?._id}`)}
                      title="View subject master catalog"
                    >
                      Catalog
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════════
         MODAL: Unit Drill-Down & Delivery Audit
         ═════════════════════════════════════════════════════════════════════════ */}
      {drilldownSubject && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
          onClick={() => {
            setDrilldownSubject(null);
            setDrilldownTeacher(null);
          }}
        >
          <div
            className="modal-container"
            style={{
              background: "#fff",
              borderRadius: 14,
              maxWidth: 780,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "1.25rem 1.5rem",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 4 }}>
                  <span className="badge badge-blue">
                    {drilldownSubject.course} · Year {drilldownSubject.yearNumber}
                  </span>
                  <StatusBadge status={drilldownSubject.status || "Not Started"} />
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                  {drilldownSubject.subjectName}
                </h2>
                {drilldownTeacher && (
                  <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>
                    Faculty: <strong>{drilldownTeacher.fullName}</strong> ({drilldownTeacher.employeeCode} · {drilldownTeacher.department})
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setDrilldownSubject(null);
                  setDrilldownTeacher(null);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "var(--text-muted)",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: "1.25rem 1.5rem" }}>
              {/* Summary Stats Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "0.75rem",
                  marginBottom: "1.25rem",
                  background: "#f8fafc",
                  padding: "0.85rem",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Completion</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#003b67" }}>
                    {drilldownSubject.completionPercentage || 0}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Units Completed</div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {drilldownSubject.completedUnits || 0} / {drilldownSubject.totalUnits || 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Planned Hours</div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {drilldownSubject.plannedHours || 0}h
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Actual Hours Taken</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color:
                        drilldownSubject.actualHours > drilldownSubject.plannedHours ? "#dc2626" : "#16a34a",
                    }}
                  >
                    {drilldownSubject.actualHours || 0}h
                  </div>
                </div>
              </div>

              {/* Units List */}
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <FiLayers size={14} /> Unit-by-Unit Delivery Log
              </h3>

              {(!drilldownSubject.units || drilldownSubject.units.length === 0) ? (
                <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)", fontSize: 13 }}>
                  No unit syllabus recorded for this subject.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                  {drilldownSubject.units.map((unit) => {
                    const unitDiff = (Number(unit.actualHours) || 0) - (Number(unit.plannedHours) || 0);
                    const isDone = unit.status === "Completed";
                    const isProg = unit.status === "In Progress";

                    return (
                      <div
                        key={unit.unitNo}
                        style={{
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          padding: "0.85rem",
                          background: isDone ? "#f0fdf4" : isProg ? "#e8f1f8" : "#fff",
                          borderLeft: `4px solid ${isDone ? "#16a34a" : isProg ? "#003b67" : "#94a3b8"}`,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                          <div>
                            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginRight: 6 }}>
                              UNIT {unit.unitNo}
                            </span>
                            <span style={{ fontSize: 13.5, fontWeight: 700 }}>{unit.title}</span>
                          </div>
                          <span
                            style={{
                              background: isDone ? "#dcfce7" : isProg ? "#e8f1f8" : "#f1f5f9",
                              color: isDone ? "#15803d" : isProg ? "#002747" : "#475569",
                              padding: "2px 8px",
                              borderRadius: 99,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {unit.status}
                          </span>
                        </div>

                        {/* Hours & Completion Dates */}
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            fontSize: 12,
                            color: "var(--text-muted)",
                            margin: "6px 0",
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            Planned: <strong>{unit.plannedHours || 0} hrs</strong>
                          </div>
                          <div>
                            Actual: <strong style={{ color: isDone && unitDiff > 0 ? "#dc2626" : "inherit" }}>{unit.actualHours || 0} hrs</strong>
                            {isDone && unitDiff !== 0 && (
                              <span style={{ marginLeft: 4, fontWeight: 700, color: unitDiff > 0 ? "#dc2626" : "#16a34a" }}>
                                ({unitDiff > 0 ? `+${unitDiff}h` : `${unitDiff}h`})
                              </span>
                            )}
                          </div>
                          {unit.completionDate && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                              <FiCalendar size={11} /> Completed on: <strong>{unit.completionDate}</strong>
                            </div>
                          )}
                        </div>

                        {/* Topics Covered */}
                        {unit.topicsTotal && unit.topicsTotal.length > 0 && (
                          <div style={{ marginTop: 6, fontSize: 12 }}>
                            <div style={{ fontWeight: 600, color: "var(--text-muted)", marginBottom: 3 }}>
                              Topics Covered ({unit.topicsCovered?.length || 0} / {unit.topicsTotal.length}):
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                              {unit.topicsTotal.map((topic, ti) => {
                                const covered = unit.topicsCovered?.includes(topic);
                                return (
                                  <span
                                    key={ti}
                                    style={{
                                      background: covered ? "#dcfce7" : "#f1f5f9",
                                      color: covered ? "#15803d" : "#64748b",
                                      border: `1px solid ${covered ? "#86efac" : "#e2e8f0"}`,
                                      padding: "1px 7px",
                                      borderRadius: 4,
                                      fontSize: 11,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.2rem",
                                    }}
                                  >
                                    {covered ? <FiCheck size={10} /> : "○"} {topic}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Remarks */}
                        {unit.remarks && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 11.5,
                              fontStyle: "italic",
                              background: "rgba(0,0,0,0.03)",
                              padding: "4px 8px",
                              borderRadius: 4,
                            }}
                          >
                            Teacher Note: {unit.remarks}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "1rem 1.5rem",
                borderTop: "1px solid var(--border)",
                background: "#f8fafc",
              }}
            >
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setDrilldownSubject(null);
                  setDrilldownTeacher(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
